import PropTypes from "prop-types";
import React from "react";
import { FormControl } from "react-bootstrap";
import { useIntl } from "react-intl";
import ObjectUtils, { coalesce } from "../../../../../utils/ObjectUtils";
import { findAnswer, isSimpleType, makeAnswer, makeSimpleAnswer } from "../../../../../utils/QuestionUtils";
import Checkbox from "../../../../common/Checkbox";
import InfoTooltip from "./InfoTooltip";

const { getDeepProperty, opt } = ObjectUtils;
const OTHER_ANSWER = "OtherAnswer";

const CheckboxRenderer = ({
  disabled,
  question,
  collection,
  addQuestionAnswer,
  setQuestionAnswer,
  removeQuestionAnswer,
  row,
  column,
  showOnlyChecked,
  defaultStyle
}) => {
  const intl = useIntl();
  const choices = coalesce(getDeepProperty(column, "choices"), getDeepProperty(question, "choices"), []);
  const hasOther = coalesce(getDeepProperty(column, "hasOther"), getDeepProperty(question, "hasOther"), false);
  const simpleAnswer = isSimpleType(question.type);

  const otherAnswer = findAnswer(collection, {
    row,
    column,
    name: OTHER_ANSWER
  });
  const otherProps = opt(otherAnswer).name === OTHER_ANSWER ? { checked: true } : {};

  const handleSelect = (choice, checked) => {
    if (checked) {
      removeQuestionAnswer(question, simpleAnswer?makeSimpleAnswer(choice.value,question):makeAnswer(choice.value, row, column));
    } else {
      handleExclusive(choice.name);

      addQuestionAnswer(question, simpleAnswer?makeSimpleAnswer(choice.value,question):makeAnswer(choice.value, row, column));
    }
  };

  const handleOtherSelect = () => {
    if (otherAnswer) {
      removeQuestionAnswer(question, simpleAnswer?makeSimpleAnswer(undefined,question,OTHER_ANSWER):makeAnswer(undefined, row, column, OTHER_ANSWER));
    } else {
      addQuestionAnswer(question,simpleAnswer?makeSimpleAnswer(undefined,question,OTHER_ANSWER):makeAnswer(undefined, row, column, OTHER_ANSWER));
      handleExclusive(OTHER_ANSWER);
    }
  };

  const handleExclusive = (name) => {
    const propertyToSearch = question.choices ? question : column;
    const exclusive = coalesce(
      getDeepProperty(propertyToSearch, "tempChoiceExclusive"),
      getDeepProperty(propertyToSearch, "choiceExclusive")
    );
    if (exclusive) {
      if (exclusive === name) {
        const selected = choices.filter(
          (c) =>
            c.name !== name &&
            !!findAnswer(collection, {
              row,
              column,
              value: c.value
            })
        );
        selected.forEach((c) => removeQuestionAnswer(question, simpleAnswer?makeSimpleAnswer(c.value,question):makeAnswer(c.value, row, column)));

        if (otherAnswer) {
          removeQuestionAnswer(question, simpleAnswer?makeSimpleAnswer(undefined,question,OTHER_ANSWER):makeAnswer(undefined, row, column, OTHER_ANSWER));
        }
      } else {
        const selectedExclusives = choices.filter(
          (c) =>
            c.name === exclusive &&
            !!findAnswer(collection, {
              row,
              column,
              value: c.value
            })
        );
        selectedExclusives.forEach((c) => removeQuestionAnswer(question, simpleAnswer?makeSimpleAnswer(c.value,question):makeAnswer(c.value, row, column)));
      }
    }
  };

  const handleOtherChange = (event) => {
    removeQuestionAnswer(question, simpleAnswer?makeSimpleAnswer(undefined,question,OTHER_ANSWER):makeAnswer(undefined, row, column, OTHER_ANSWER));
    addQuestionAnswer(question, simpleAnswer?makeSimpleAnswer(event.target.value,question,OTHER_ANSWER):makeAnswer(event.target.value, row, column, OTHER_ANSWER));
  };

  const renderCheckbox = (question, choice, i) => {
    const checked = !!findAnswer(collection, {
      row,
      column,
      value: choice.value
    });

    return (
      ((showOnlyChecked && checked) || !showOnlyChecked) && (
        <div key={`${choice.value}-${i}`}>
          <Checkbox
            type={defaultStyle ? "checkbox" : "toggle"}
            disabled={disabled}
            onChange={() => handleSelect(choice, checked)}
            checked={checked}
            inline={!!choice.infoText}
          >
            {choice.text}
          </Checkbox>
          {choice.infoText && <InfoTooltip infoText={choice.infoText} />}
        </div>
      )
    );
  };

  return (
    <div>
      {choices.map((choice, i) => renderCheckbox(question, choice, i))}
      {hasOther && (
        <>
          <Checkbox
            type={defaultStyle ? "checkbox" : "toggle"}
            disabled={disabled}
            onChange={handleOtherSelect}
            {...otherProps}
          >
            {intl.formatMessage({ id: "user.surveyView.radio.otherChoice.input" })}
          </Checkbox>
        </>
      )}
      {otherAnswer?.name === OTHER_ANSWER && (
        <FormControl
          type="text"
          readOnly={disabled}
          placeholder={intl.formatMessage({ id: "user.surveyView.radio.otherChoice" })}
          onBlur={handleOtherChange}
          defaultValue={otherAnswer?.value ?? ""}
        />
      )}
    </div>
  );
};

CheckboxRenderer.propTypes = {
  disabled: PropTypes.bool,
  question: PropTypes.object.isRequired,
  collection: PropTypes.array,
  addQuestionAnswer: PropTypes.func.isRequired,
  setQuestionAnswer: PropTypes.func.isRequired,
  removeQuestionAnswer: PropTypes.func.isRequired,
  textValue: PropTypes.string,
  checked: PropTypes.bool,
  row: PropTypes.object,
  column: PropTypes.object,
  intl: PropTypes.object,
  showOnlyChecked: PropTypes.bool
};

export default CheckboxRenderer;
